import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import Fade from "@mui/material/Fade";
import Zoom from "@mui/material/Zoom";
import { styled, useTheme, useColorScheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import LogoutIcon from "@mui/icons-material/Logout";
import AppTheme from "../shared-theme/AppTheme";
import { useTranslation } from "react-i18next";
import "../i18n/i18n";
import { getCurrentTenant } from "../config/tenantConfig";
import KEYCLOAK_CONFIG from "../config/keycloakConfig";

const { apiBaseUrl: API_BASE_URL } = getCurrentTenant();

// Glassmorphism Card
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(2.5),
  gap: theme.spacing(2),
  margin: "auto",
  maxWidth: "520px",
  borderRadius: "16px",
  backdropFilter: "blur(20px)",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(30, 30, 40, 0.7)"
      : "rgba(255, 255, 255, 0.8)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
      : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  maxHeight: "calc(100dvh - 32px)",
  overflowY: "auto",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(5),
    gap: theme.spacing(3),
    borderRadius: "24px",
    maxHeight: "calc(100dvh - 64px)",
  },
  animation: "fadeInUp 0.8s ease-out",
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

// Background Container
const ClientSelectContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  position: "relative",
  overflowX: "hidden",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  background:
    theme.palette.mode === "dark"
      ? "radial-gradient(at 50% 0%, hsla(150, 20%, 20%, 1) 0, transparent 70%), radial-gradient(at 50% 100%, hsla(150, 25%, 10%, 1) 0, transparent 70%)"
      : `
      radial-gradient(at 50% 50%, #ffffff 0%, transparent 60%),
      radial-gradient(at 0% 0%, hsla(145, 60%, 96%, 1) 0, transparent 50%),
      radial-gradient(at 100% 100%, hsla(145, 60%, 94%, 1) 0, transparent 50%)
    `,
  backgroundColor: theme.palette.mode === "dark" ? "#0d1f0d" : "#f0fdf4",
}));

function parseJwt(token) {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT format");
  const base64 =
    parts[1].replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice(2 - (parts[1].length % 4));
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(json);
}

// Force light mode
function SystemModeSync() {
  const { setMode } = useColorScheme();
  useEffect(() => {
    setMode("light");
  }, [setMode]);
  return null;
}

export default function ClientSelect(props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const tenantConfig = getCurrentTenant();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [decodedToken, setDecodedToken] = useState(null);

  // Client oluşturma
  const [newClientType, setNewClientType] = useState(null); // 0 = Kurumsal, 1 = Bireysel
  const [newClientFormData, setNewClientFormData] = useState({
    clientName: "",
    vkn_tckn: "",
    address: "",
  });
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem("authToken");

  // Token'dan client listesini al
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const decoded = parseJwt(token);
      setDecodedToken(decoded);

      const clients = Object.keys(decoded.resource_access || {});
      const userClients = clients.filter(
        (c) => c !== "account" && c !== "realm-management"
      );

      setAvailableClients(userClients);
      if (userClients.length > 0) {
        setSelectedClient(userClients[0]);
      }

      // AllowedSignUp "0" ve tek client varsa otomatik giriş yap
      if (decoded.AllowedSignUp === "0" && userClients.length === 1) {
        console.log("[ClientSelect] === AUTO-LOGIN: AllowedSignUp=0, single client ===");
        console.log("[ClientSelect] Auto-selecting client:", userClients[0]);
        autoSelectClient(token, userClients[0]);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error("Token parse error:", err);
      setError("Token okunamadı");
      setLoading(false);
    }
  }, [token, navigate]);

  // Objects ve Enums verilerini çekip localStorage'a kaydet
  const fetchObjectsAndEnums = async (accessToken, projectCode) => {
    const lang = i18n.language || "tr";
    const languageType =
      lang.startsWith("tr") ? 1 : lang.startsWith("de") ? 3 : lang.startsWith("fr") ? 4 : lang.startsWith("es") ? 5 : 2;

    try {
      const [objectResponse, enumResponse] = await Promise.all([
        fetch(
          `${API_BASE_URL}/${projectCode}/api/Objects?lettaLanguageType=${languageType}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        ),
        fetch(`${API_BASE_URL}/${projectCode}/api/Enums`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      ]);

      if (objectResponse.ok) {
        const objectData = await objectResponse.json();
        localStorage.setItem("objectData", JSON.stringify(objectData.data || []));
      }
      if (enumResponse.ok) {
        const enumData = await enumResponse.json();
        localStorage.setItem("enumData", JSON.stringify(enumData.data || []));
      }
    } catch (err) {
      console.error("Objects/Enums fetch error:", err);
    }
  };

  // AllowedSignUp=0 ve tek client varsa otomatik token exchange + giriş
  const autoSelectClient = async (subjectToken, clientName) => {
    try {
      console.log("[ClientSelect] Auto-login token exchange, audience:", clientName);
      const params = new URLSearchParams({
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: KEYCLOAK_CONFIG.clientId,
        subject_token: subjectToken,
        audience: clientName,
        requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
      });

      const res = await fetch(
        `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        }
      );

      console.log("[ClientSelect] Auto-login exchange response status:", res.status);
      const data = await res.json();

      if (data.access_token) {
        console.log("[ClientSelect] Auto-login token exchange SUCCESS");
        const decoded = parseJwt(data.access_token);
        const username = decoded.email || decoded.preferred_username || decoded.sub;
        const adminId = decoded.adminId;
        const clientRoles = decoded.resource_access?.[clientName]?.roles || [];
        const projectCode = tenantConfig?.projectCode;

        localStorage.setItem("authToken", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
        }
        localStorage.setItem("userName", username);
        localStorage.setItem("companyName", clientName);
        localStorage.setItem("projectCode", projectCode);
        localStorage.setItem("userRoles", JSON.stringify(clientRoles));
        if (adminId !== undefined) {
          localStorage.setItem("adminId", parseInt(adminId, 10));
        }
        localStorage.setItem(clientName, JSON.stringify({ selected: true }));

        await fetchObjectsAndEnums(data.access_token, projectCode);

        console.log("[ClientSelect] === AUTO-LOGIN COMPLETE, navigating to /dashboard ===");
        navigate("/dashboard");
      } else {
        console.error("[ClientSelect] Auto-login exchange FAILED:", data.error, data.error_description);
        setLoading(false);
      }
    } catch (err) {
      console.error("[ClientSelect] Auto-login error:", err);
      setLoading(false);
    }
  };

  // Client seçimi ile devam et
  const handleSelectClient = async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      // Token exchange yap
      console.log("[ClientSelect] === SELECT CLIENT TOKEN EXCHANGE ===");
      console.log("[ClientSelect] audience:", selectedClient);
      console.log("[ClientSelect] client_id:", KEYCLOAK_CONFIG.clientId);

      const params = new URLSearchParams({
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: KEYCLOAK_CONFIG.clientId,
        subject_token: token,
        audience: selectedClient,
        requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
      });

      const res = await fetch(
        `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        }
      );

      console.log("[ClientSelect] Exchange response status:", res.status);
      const data = await res.json();

      if (data.access_token) {
        console.log("[ClientSelect] Token exchange SUCCESS for client:", selectedClient);
        const newDecodedToken = parseJwt(data.access_token);
        const username = newDecodedToken.email || newDecodedToken.preferred_username || newDecodedToken.sub;
        const adminId = newDecodedToken.adminId;
        const clientRoles = newDecodedToken.resource_access?.[selectedClient]?.roles || [];
        const projectCode = tenantConfig?.projectCode;
        console.log("[ClientSelect] Decoded - username:", username, "adminId:", adminId, "roles:", clientRoles);

        // localStorage'a kaydet
        localStorage.setItem("authToken", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
          console.log("[ClientSelect] refresh_token saved");
        }
        localStorage.setItem("userName", username);
        localStorage.setItem("companyName", selectedClient);
        localStorage.setItem("projectCode", projectCode);
        localStorage.setItem("userRoles", JSON.stringify(clientRoles));
        if (adminId !== undefined) {
          localStorage.setItem("adminId", parseInt(adminId, 10));
        }
        // Client verisi olarak kaydet (ProtectedRoutes kontrolü için)
        localStorage.setItem(selectedClient, JSON.stringify({ selected: true }));

        // Objects ve Enums verilerini çek
        await fetchObjectsAndEnums(data.access_token, projectCode);

        console.log("[ClientSelect] === SELECT CLIENT COMPLETE, navigating to /dashboard ===");
        // Dashboard'a git
        navigate("/dashboard");
      } else {
        console.error("[ClientSelect] Token exchange FAILED:", data.error, data.error_description);
        throw new Error(data.error_description || "Token exchange başarısız");
      }
    } catch (err) {
      console.error("[ClientSelect] Client selection error:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Yeni client oluştur
  const handleCreateClient = async () => {
    if (!newClientFormData.clientName) {
      setError("Client adı gerekli");
      return;
    }
    if (newClientType === 0 && !newClientFormData.vkn_tckn) {
      setError("Vergi Kimlik Numarası gerekli");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Önce token exchange yap (CreateAdmin API için)
      console.log("[ClientSelect] === CREATE CLIENT - INITIAL TOKEN EXCHANGE ===");
      console.log("[ClientSelect] audience:", KEYCLOAK_CONFIG.clientId, "(DefaultClient)");

      const params = new URLSearchParams({
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: KEYCLOAK_CONFIG.clientId,
        subject_token: token,
        audience: KEYCLOAK_CONFIG.clientId,
        requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
      });

      const exchangeRes = await fetch(
        `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        }
      );

      console.log("[ClientSelect] Initial exchange response status:", exchangeRes.status);
      const exchangeData = await exchangeRes.json();

      if (!exchangeData.access_token) {
        console.error("[ClientSelect] Initial exchange FAILED:", exchangeData.error, exchangeData.error_description);
        throw new Error("Token exchange başarısız");
      }
      console.log("[ClientSelect] Initial exchange SUCCESS");

      // Backend'e client oluşturma isteği
      const createRes = await fetch(
        `${API_BASE_URL}/${tenantConfig?.projectCode}/api/WebUser/CreateAdmin`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${exchangeData.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            clientName: newClientFormData.clientName,
            vkn_tckn: newClientType === 0 ? newClientFormData.vkn_tckn : "",
            address: newClientFormData.address,
            type: newClientType,
          }),
        }
      );

      // Response'u kontrol et
      const responseText = await createRes.text();
      let createData;

      try {
        createData = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error("JSON parse error:", parseError, "Response:", responseText);
        throw new Error("Sunucudan geçersiz yanıt alındı");
      }

      // HTTP status kontrolü
      if (!createRes.ok) {
        throw new Error(createData.exMessage || createData.message || `HTTP ${createRes.status}: İstek başarısız`);
      }

      if (createData.returnCode === 0 || createRes.ok) {
        // Başarılı - yeni client ile devam et
        const createdClientId = createData?.data?.clientId || newClientFormData.clientName;
        setSelectedClient(createdClientId);
        setAvailableClients((prev) => [...prev, createdClientId]);
        setNewClientType(null);
        setNewClientFormData({ clientName: "", vkn_tckn: "", address: "" });

        // Yeni client için token exchange yap (client scope'lu token al)
        console.log("[ClientSelect] === CREATE CLIENT - NEW CLIENT TOKEN EXCHANGE ===");
        console.log("[ClientSelect] audience:", createdClientId);
        const newClientParams = new URLSearchParams({
          grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
          client_id: KEYCLOAK_CONFIG.clientId,
          subject_token: token,
          audience: createdClientId,
          requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
        });

        const newClientExchangeRes = await fetch(
          `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: newClientParams,
          }
        );

        console.log("[ClientSelect] New client exchange response status:", newClientExchangeRes.status);
        const newClientExchangeData = await newClientExchangeRes.json();

        if (!newClientExchangeData.access_token) {
          console.error("[ClientSelect] New client exchange FAILED:", newClientExchangeData.error, newClientExchangeData.error_description);
          throw new Error("Yeni client için token exchange başarısız");
        }
        console.log("[ClientSelect] New client exchange SUCCESS");

        const newDecodedToken = parseJwt(newClientExchangeData.access_token);
        const username = newDecodedToken.email || newDecodedToken.preferred_username || newDecodedToken.sub;
        const adminId = newDecodedToken.adminId;
        const clientRoles = newDecodedToken.resource_access?.[createdClientId]?.roles || [];
        const projectCode = tenantConfig?.projectCode;

        // Token'ı güncelle (yeni client scope'lu)
        localStorage.setItem("authToken", newClientExchangeData.access_token);
        if (newClientExchangeData.refresh_token) {
          localStorage.setItem("refreshToken", newClientExchangeData.refresh_token);
        }
        localStorage.setItem("userName", username);
        localStorage.setItem("companyName", createdClientId);
        localStorage.setItem("projectCode", projectCode);
        localStorage.setItem("userRoles", JSON.stringify(clientRoles));
        if (adminId !== undefined) {
          localStorage.setItem("adminId", parseInt(adminId, 10));
        }
        // Client verisi olarak kaydet (ProtectedRoutes kontrolü için)
        localStorage.setItem(createdClientId, JSON.stringify({ selected: true }));

        console.log("[ClientSelect] Decoded - username:", username, "adminId:", adminId, "roles:", clientRoles);

        // Objects ve Enums verilerini çek
        await fetchObjectsAndEnums(newClientExchangeData.access_token, projectCode);

        console.log("[ClientSelect] === CREATE CLIENT COMPLETE, navigating to /dashboard ===");
        navigate("/dashboard");
      } else {
        throw new Error(createData.exMessage || "Client oluşturma başarısız");
      }
    } catch (err) {
      console.error("Create client error:", err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Başka hesap ile giriş yap - tüm session'ları temizle
  const handleLogoutAndLogin = () => {
    // Keycloak logout URL oluştur (silmeden ÖNCE oku)
    const idToken = localStorage.getItem("idToken");

    // localStorage'daki tüm auth verilerini temizle
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("companyName");
    localStorage.removeItem("projectCode");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("adminId");
    localStorage.removeItem("objectData");
    localStorage.removeItem("enumData");
    localStorage.removeItem("pendingConfirmRole");
    const postLogoutRedirectUri = `${window.location.origin}/login`;

    let logoutUrl = `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}&client_id=${KEYCLOAK_CONFIG.clientId}`;

    if (idToken) {
      logoutUrl += `&id_token_hint=${idToken}`;
    }

    // Keycloak logout sayfasına yönlendir
    window.location.href = logoutUrl;
  };

  // Loading state
  if (loading) {
    return (
      <AppTheme {...props}>
        <SystemModeSync />
        <CssBaseline enableColorScheme />
        <ClientSelectContainer
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Card variant="outlined">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {t("common.loading") || "Yükleniyor..."}
              </Typography>
            </Box>
          </Card>
        </ClientSelectContainer>
      </AppTheme>
    );
  }

  return (
    <AppTheme {...props}>
      <SystemModeSync />
      <CssBaseline enableColorScheme />
      <ClientSelectContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: { xs: 1, sm: 2 },
            }}
          >
            <Zoom in={true} timeout={600}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 1, sm: 2 } }}>
                <GroupsIcon
                  sx={{
                    fontSize: { xs: 40, sm: 56 },
                    color: "#2196F3",
                    transition: "transform 0.3s ease, color 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                      color: "#21CBF3",
                    },
                  }}
                />
              </Box>
            </Zoom>
            <Fade in={true} timeout={600} style={{ transitionDelay: "200ms" }}>
              <Typography
                fontWeight="700"
                sx={{
                  textAlign: "center",
                  background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 0.5,
                  fontSize: { xs: "1.4rem", sm: "2.125rem" },
                }}
              >
                {t("clientSelect.title") || "Client Seçimi"}
              </Typography>
            </Fade>
            <Fade in={true} timeout={600} style={{ transitionDelay: "400ms" }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, textAlign: "center" }}
              >
                {t("clientSelect.description") ||
                  "Devam etmek için bir client seçin veya yeni bir client oluşturun."}
              </Typography>
            </Fade>
          </Box>

          {/* Error */}
          {error && (
            <Fade in={!!error} timeout={300}>
              <Box
                sx={{
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  border: "1px solid rgba(244, 67, 54, 0.3)",
                  borderRadius: "12px",
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ color: "#f44336", fontSize: "0.875rem", textAlign: "center" }}
                >
                  {error}
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Mevcut Client Seçimi */}
          {availableClients.length > 0 && (
            <Fade in={true} timeout={600} style={{ transitionDelay: "300ms" }}>
              <Box>
                <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <FormLabel>{t("clientSelect.existingClients") || "Mevcut Client'lar"}</FormLabel>
                  <TextField
                    select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    variant="outlined"
                    fullWidth
                  >
                    {availableClients.map((client) => (
                      <MenuItem key={client} value={client}>
                        {client}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (!selectedClient || loading) return;
                    handleSelectClient();
                  }}
                  sx={{
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "12px",
                    border: "none",
                    background: (!selectedClient || loading)
                      ? "#bdbdbd"
                      : "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    boxShadow: (!selectedClient || loading)
                      ? "none"
                      : "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    color: "#fff",
                    cursor: (!selectedClient || loading) ? "default" : "pointer",
                    "&:hover": {
                      background: (!selectedClient || loading)
                        ? "#bdbdbd"
                        : "linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)",
                      transform: (!selectedClient || loading) ? "none" : "scale(1.02)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("clientSelect.continue") || "Devam Et"
                  )}
                </Button>
              </Box>
            </Fade>
          )}

          {/* Divider */}
          {availableClients.length > 0 && decodedToken?.AllowedSignUp !== "0" && (
            <Divider sx={{ my: { xs: 1, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary">
                {t("signIn.or") || "veya"}
              </Typography>
            </Divider>
          )}

          {/* Yeni Client Oluştur */}
          {decodedToken?.AllowedSignUp !== "0" && (
          <Fade in={true} timeout={600} style={{ transitionDelay: "500ms" }}>
            <Box>
              <Typography sx={{ mb: 2, fontWeight: 600 }}>
                {t("clientSelect.createNew") || "Yeni Client Oluştur"}
              </Typography>

              <ToggleButtonGroup
                value={newClientType}
                exclusive
                onChange={(e, val) => {
                  if (val !== null) {
                    setNewClientType(val);
                    setNewClientFormData({ clientName: "", vkn_tckn: "", address: "" });
                  }
                }}
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton
                  value={0}
                  sx={{
                    gap: { xs: 0.5, sm: 1 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    textTransform: "none",
                    fontWeight: "bold",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(33, 150, 243, 0.12)",
                      color: "primary.main",
                    },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: { xs: 18, sm: 24 } }} /> {t("clientSelect.corporate") || "Kurumsal"}
                </ToggleButton>
                <ToggleButton
                  value={1}
                  sx={{
                    gap: { xs: 0.5, sm: 1 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    textTransform: "none",
                    fontWeight: "bold",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(76, 175, 80, 0.12)",
                      color: "success.main",
                    },
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 18, sm: 24 } }} /> {t("clientSelect.individual") || "Bireysel"}
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Kurumsal Form */}
              {newClientType === 0 && (
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel>{t("clientSelect.clientName") || "Client Adı"} *</FormLabel>
                    <TextField
                      value={newClientFormData.clientName}
                      onChange={(e) =>
                        setNewClientFormData((prev) => ({
                          ...prev,
                          clientName: e.target.value,
                        }))
                      }
                      variant="outlined"
                      fullWidth
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel>{t("clientSelect.taxNumber") || "Vergi Kimlik Numarası"} *</FormLabel>
                    <TextField
                      value={newClientFormData.vkn_tckn}
                      onChange={(e) =>
                        setNewClientFormData((prev) => ({
                          ...prev,
                          vkn_tckn: e.target.value,
                        }))
                      }
                      variant="outlined"
                      fullWidth
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel>{t("clientSelect.address") || "Adres"} *</FormLabel>
                    <TextField
                      value={newClientFormData.address}
                      onChange={(e) =>
                        setNewClientFormData((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </FormControl>
                </Box>
              )}

              {/* Bireysel Form */}
              {newClientType === 1 && (
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel>{t("clientSelect.clientName") || "Client Adı"} *</FormLabel>
                    <TextField
                      value={newClientFormData.clientName}
                      onChange={(e) =>
                        setNewClientFormData((prev) => ({
                          ...prev,
                          clientName: e.target.value,
                        }))
                      }
                      variant="outlined"
                      fullWidth
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel>{t("clientSelect.address") || "Adres"}</FormLabel>
                    <TextField
                      value={newClientFormData.address}
                      onChange={(e) =>
                        setNewClientFormData((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </FormControl>
                </Box>
              )}

              {/* Oluştur Butonu */}
              {newClientType !== null && (() => {
                // Kurumsal için 3 alan zorunlu, Bireysel için sadece clientName
                const isFormValid = newClientType === 0
                  ? (newClientFormData.clientName && newClientFormData.vkn_tckn && newClientFormData.address)
                  : newClientFormData.clientName;
                const isDisabled = creating || !isFormValid;

                return (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (isDisabled) return;
                    handleCreateClient();
                  }}
                  sx={{
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "12px",
                    border: "none",
                    background: isDisabled
                      ? "#bdbdbd"
                      : "linear-gradient(45deg, #4CAF50 30%, #81C784 90%)",
                    boxShadow: isDisabled
                      ? "none"
                      : "0 3px 5px 2px rgba(76, 175, 80, .3)",
                    color: "#fff",
                    cursor: isDisabled ? "default" : "pointer",
                    "&:hover": {
                      background: isDisabled
                        ? "#bdbdbd"
                        : "linear-gradient(45deg, #388E3C 30%, #66BB6A 90%)",
                      transform: isDisabled ? "none" : "scale(1.02)",
                    },
                  }}
                >
                  {creating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("clientSelect.createAndContinue") || "Oluştur ve Devam Et"
                  )}
                </Button>
                );
              })()}
            </Box>
          </Fade>
          )}

          {/* Başka Hesap ile Giriş Yap */}
          <Divider sx={{ my: { xs: 1.5, sm: 3 } }} />
          <Fade in={true} timeout={600} style={{ transitionDelay: "600ms" }}>
            <Button
              fullWidth
              variant="text"
              onClick={handleLogoutAndLogin}
              startIcon={<LogoutIcon />}
              sx={{
                py: { xs: 1, sm: 1.5 },
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                fontWeight: 600,
                textTransform: "none",
                color: "#f44336",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.08)",
                  color: "#d32f2f",
                },
              }}
            >
              {t("clientSelect.loginWithAnotherAccount") || "Başka bir hesap ile giriş yap"}
            </Button>
          </Fade>
        </Card>
      </ClientSelectContainer>
    </AppTheme>
  );
}
